// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/configuracoes/services/configNfseService.ts
//  Configuração de integração NFS-e Nacional (ADN/SEFIN gov.br).
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';

export interface ConfigNfse {
  empresaId:              number;
  emiteNfse:              boolean;
  numeroRps?:             number;
  numeroNfse?:            number;
  usaServidorNacional:    boolean;
  serieRps?:              string;
  serieNfse?:             string;
  /** 1 = Produção, 2 = Homologação (Produção restrita) */
  tipoAmbiente?:          number;
  diretorioXml?:          string;
  diretorioPdf?:          string;
  cidadeHomologada?:      number;
  incentivadorCultural:   boolean;
  // Integração ADN
  certificadoCaminho?:    string;
  certificadoThumbprint?: string;
  certificadoValidade?:   string;
  urlAdnBase?:            string;
  urlSefinBase?:          string;
  certificadoConfigurado: boolean;
  // Credenciais provedor (prefeituras municipais) — segredos nunca retornam
  // em claro; a API devolve apenas a flag "configurado".
  usuarioWebService?:     string;
  fraseSecretaConfigurada:     boolean;
  chaveAcessoConfigurada:      boolean;
  chaveAutorizacaoConfigurada: boolean;
  codigoSiafi?:           string;
  // Tempos / retentativa
  aguardarRetorno?:       number;
  tentativas?:            number;
  intervaloTentativas?:   number;
  ibAjusteTempos:         boolean;
  diretorioArquivoCidades?: string;
  // Prefeitura
  cnpjPrefeitura?:        string;
  nomePrefeitura?:        string;
  logoPrefeituraConfigurado: boolean;
}

export interface SalvarConfigNfseRequest {
  emiteNfse:              boolean;
  numeroRps?:             number;
  numeroNfse?:            number;
  usaServidorNacional:    boolean;
  serieRps?:              string;
  serieNfse?:             string;
  tipoAmbiente?:          number;
  diretorioXml?:          string;
  diretorioPdf?:          string;
  cidadeHomologada?:      number;
  incentivadorCultural:   boolean;
  certificadoCaminho?:    string;
  /** Só envie quando alterar — null/undefined preserva o valor atual. */
  certificadoSenha?:      string;
  certificadoThumbprint?: string;
  urlAdnBase?:            string;
  urlSefinBase?:          string;
  // Credenciais provedor — segredos só enviam ao alterar (undefined preserva)
  usuarioWebService?:     string;
  senhaWebService?:       string;
  fraseSecreta?:          string;
  chaveAcesso?:           string;
  chaveAutorizacao?:      string;
  codigoSiafi?:           string;
  aguardarRetorno?:       number;
  tentativas?:            number;
  intervaloTentativas?:   number;
  ibAjusteTempos:         boolean;
  diretorioArquivoCidades?: string;
  cnpjPrefeitura?:        string;
  nomePrefeitura?:        string;
  /** null/undefined preserva; "" remove; base64 substitui. */
  logoPrefeituraBase64?:  string;
}

export interface ValidacaoCertificado {
  sucesso:          boolean;
  subject?:         string;
  emissor?:         string;
  validoDe?:        string;
  validoAte?:       string;
  diasParaExpirar?: number;
  mensagem?:        string;
}

const BASE = '/api/config/nfse';

export const configNfseService = {
  async obter(empresaId: number): Promise<ConfigNfse | null> {
    try {
      const { data } = await apiClient.get<ConfigNfse>(`${BASE}/${empresaId}`);
      return data;
    } catch (err: unknown) {
      // 404 = não configurado ainda — devolve null
      const e = err as { response?: { status?: number } };
      if (e.response?.status === 404) return null;
      throw err;
    }
  },

  async salvar(empresaId: number, req: SalvarConfigNfseRequest): Promise<ConfigNfse> {
    const { data } = await apiClient.put<ConfigNfse>(`${BASE}/${empresaId}`, req);
    return data;
  },

  async validarCertificado(empresaId: number): Promise<ValidacaoCertificado> {
    const { data } = await apiClient.post<ValidacaoCertificado>(`${BASE}/${empresaId}/validar`);
    return data;
  },
};
